// src/context/AuthContext.js
import React, { createContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GoogleAuthProvider,
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendSignInLinkToEmail,
  sendPasswordResetEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
} from 'firebase/auth';
import { auth } from '../firebase';

export const AuthContext = createContext({
  user: null,
  accessToken: null,
  authReady: false,
  error: null,
  pendingLink: false,
  clearError: () => {},
  signInWithGoogle: async () => {},
  signInWithEmail: async () => {},
  sendMagicLink: async () => {},
  resetPassword: async () => {},
  completeLinkSignIn: async () => {},
  signOut: async () => {},
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [error, setError] = useState(null);
  const [pendingLink, setPendingLink] = useState(false);
  const navigate = useNavigate();

  const actionCodeSettings = {
    url: `${window.location.origin}/signin`,
    handleCodeInApp: true,
  };

  const clearPendingLink = () => setPendingLink(false);

  // Listen for Firebase user changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setAuthReady(true);
    });
    return unsubscribe;
  }, []);

  // Detect magic-link arrival
  useEffect(() => {
    if (isSignInWithEmailLink(auth, window.location.href)) {
      setPendingLink(true);
    }
  }, []);

  // Complete magic-link sign-in when UI supplies the email
  const completeLinkSignIn = async (email) => {
    try {
      const { user: fbUser } = await signInWithEmailLink(
        auth,
        email,
        window.location.href
      );
      setUser(fbUser);
      window.localStorage.removeItem('magicEmail');
      setError(null);
      setPendingLink(false);
      navigate('/generate');
    } catch (e) {
      console.error('Error finishing email link sign-in:', e);
      if (e.code === 'auth/invalid-action-code') {
          setError(
            new Error(
              'This link is no longer valid. It may have expired or been used already. Please request a new sign-in link.'
            )
          );
        } else {
          setError(e);
        }
    }
  };

    // Password-reset e-mail
  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email, {
        url: `${window.location.origin}/signin`,
      });
      setError(null);
    } catch (err) {
      console.error('Reset-password failed:', err);
      setError(err);
      throw err;
    }
  };

  // Google OAuth sign-in
  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    provider.addScope('https://www.googleapis.com/auth/classroom.courses');
    provider.addScope('https://www.googleapis.com/auth/classroom.coursework.me');
    provider.addScope('https://www.googleapis.com/auth/classroom.courseworkmaterials');
    provider.addScope('https://www.googleapis.com/auth/classroom.topics');
    provider.addScope('https://www.googleapis.com/auth/classroom.coursework.students');
    provider.addScope('https://www.googleapis.com/auth/classroom.announcements');
    provider.addScope('https://www.googleapis.com/auth/devstorage.full_control');
    provider.addScope('https://www.googleapis.com/auth/forms.body');
    provider.setCustomParameters({ prompt: 'consent' });

    try {
      const result = await signInWithPopup(auth, provider);
      const fbUser = result.user;
      setUser(fbUser);
      const credential = GoogleAuthProvider.credentialFromResult(result);
      const token = credential?.accessToken ?? null;
      setAccessToken(token);
      setError(null);
      return token;
    } catch (err) {
      console.error('Google sign-in failed:', err);
      setError(err);
      throw err;
    }
  };

  // Email/password sign-in (or create account if needed)
  const signInWithEmail = async (email, password, createIfNeeded = false) => {
    try {
      const { user: fbUser } = createIfNeeded
        ? await createUserWithEmailAndPassword(auth, email, password)
        : await signInWithEmailAndPassword(auth, email, password);
      setUser(fbUser);
      setError(null);
      return fbUser;
    } catch (err) {
      console.error('Email sign-in failed:', err);
      setError(err);
      if (err.code === 'auth/user-not-found' && !createIfNeeded) {
        throw new Error('no-account');
      }
      throw err;
    }
  };

  // Send magic link for password-less sign-in
  const sendMagicLink = async (email) => {
    try {
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      window.localStorage.setItem('magicEmail', email);
      setError(null);
    } catch (err) {
      console.error('Send magic-link failed:', err);
      setError(err);
      throw err;
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      setUser(null);
      setAccessToken(null);
      setError(null);
      window.location.href = '/signin';
    } catch (err) {
      console.error('Sign-out failed:', err);
      setError(err);
      throw err;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        accessToken,
        authReady,
        error,
        pendingLink,
        clearPendingLink,
        clearError: () => setError(null),
        signInWithGoogle,
        signInWithEmail,
        sendMagicLink,
        resetPassword,
        completeLinkSignIn,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
